import React, { useEffect, useState } from 'react';
import { C, LAYOUT } from '../../shared/design-tokens.js';
import { apiFetch, apiWrite } from '../../lib/api-client.js';
import { CONTROL_INPUT, MUTED_LABEL } from './fleet-ui.js';

interface TransferVehicle {
  truck_number?: string;
}

interface PendingTransfer {
  id: string | number;
  vehicles?: TransferVehicle;
  from_terminal_name?: string;
  from_terminal?: { name?: string };
  to_terminal_name?: string;
  to_terminal?: { name?: string };
  initiated_at?: string;
}

interface PendingTransfersResponse {
  transfers?: PendingTransfer[];
}

interface FleetPendingTransfersProps {
  refreshKey: number | string;
  onReceiveComplete?: () => void;
  onCancelComplete?: () => void;
}

export default function FleetPendingTransfers({ refreshKey, onReceiveComplete, onCancelComplete }: FleetPendingTransfersProps) {
  const [transfers, setTransfers] = useState<PendingTransfer[]>([]);
  const [loading, setLoading] = useState(true);
  const [receivingTransferId, setReceivingTransferId] = useState<string | number | null>(null);
  const [confirmTruckNumber, setConfirmTruckNumber] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch<PendingTransfersResponse>('/api/fleet/transfers/pending')
      .then((data) => setTransfers(data.transfers || []))
      .catch(() => setTransfers([]))
      .finally(() => setLoading(false));
  }, [refreshKey]);

  async function handleReceive() {
    if (!receivingTransferId || !confirmTruckNumber) return;
    setSubmitting(true);
    setError(null);
    const { ok, data } = await apiWrite<{ error?: string }>(`/api/fleet/transfers/${receivingTransferId}/receive`, 'PUT', {
      truck_number_confirmation: confirmTruckNumber,
    });
    setSubmitting(false);
    if (!ok) {
      setError(data?.error || 'Receive failed');
      return;
    }
    setReceivingTransferId(null);
    setConfirmTruckNumber('');
    if (onReceiveComplete) onReceiveComplete();
  }

  async function handleCancel(transferId: string | number) {
    setSubmitting(true);
    setError(null);
    const { ok, data } = await apiWrite<{ error?: string }>(`/api/fleet/transfers/${transferId}/cancel`, 'POST', {});
    setSubmitting(false);
    if (!ok) {
      setError(data?.error || 'Cancel failed');
      return;
    }
    if (onCancelComplete) onCancelComplete();
  }

  if (loading) return null;
  if (!transfers.length) return null;

  return (
    <div style={{ ...LAYOUT.subPanel, border: `1px solid rgba(201,162,39,0.3)` }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.08em', color: C.gold }}>
          Pending transfers
        </div>
        <div style={{ color: C.gold, fontSize: 13, fontWeight: 700 }}>
          {transfers.length} in transit
        </div>
      </div>

      <div style={{ display: 'grid', gap: 10 }}>
        {transfers.map((transfer) => {
          const vehicle = transfer.vehicles || {};
          return (
            <div key={transfer.id} style={{ padding: 12, borderRadius: 10, background: C.panel, border: `1px solid rgba(201,162,39,0.2)` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 6 }}>
                <div style={{ color: C.text, fontSize: 14, fontWeight: 700 }}>#{vehicle.truck_number || '—'}</div>
                <div style={{ color: C.gold, fontSize: 11, textTransform: 'uppercase' }}>In transit</div>
              </div>
              <div style={{ display: 'grid', gap: 4, gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', marginBottom: 12 }}>
                <div style={{ color: C.textMuted, fontSize: 12 }}>From: <span style={{ color: C.text }}>{transfer.from_terminal_name || transfer.from_terminal?.name || '—'}</span></div>
                <div style={{ color: C.textMuted, fontSize: 12 }}>To: <span style={{ color: C.text }}>{transfer.to_terminal_name || transfer.to_terminal?.name || '—'}</span></div>
                {transfer.initiated_at && <div style={{ color: C.textMuted, fontSize: 12 }}>Started: <span style={{ color: C.text }}>{String(transfer.initiated_at).slice(0, 10)}</span></div>}
              </div>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => setReceivingTransferId(transfer.id)}
                  style={{ flex: 1, padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.gold}`, background: 'transparent', color: C.gold, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                >
                  Receive vehicle
                </button>
                <button
                  type="button"
                  onClick={() => handleCancel(transfer.id)}
                  disabled={submitting}
                  style={{ padding: '8px 12px', borderRadius: 8, border: `1px solid ${C.red}`, background: 'transparent', color: C.red, fontSize: 12, fontWeight: 600, cursor: submitting ? 'not-allowed' : 'pointer', opacity: submitting ? 0.5 : 1 }}
                >
                  Cancel
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {receivingTransferId && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 20 }}>
          <div style={{ background: '#0d1117', border: `1px solid ${C.gold}`, borderRadius: 12, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 0 32px rgba(0,0,0,0.5)' }}>
            <div style={{ color: C.gold, fontSize: 16, fontWeight: 700, marginBottom: 8 }}>Confirm Receipt</div>
            <p style={{ color: C.textMuted, fontSize: 13, lineHeight: 1.5, marginBottom: 20 }}>
              To complete this transfer, please re-type the truck number to verify physical possession.
            </p>

            <label style={MUTED_LABEL}>Truck Number</label>
            <input
              autoFocus
              type="text"
              value={confirmTruckNumber}
              onChange={(e) => setConfirmTruckNumber(e.target.value)}
              placeholder="Enter truck number..."
              style={{ ...CONTROL_INPUT, marginBottom: 12 }}
            />

            {error && <div style={{ color: C.red, fontSize: 12, marginBottom: 12 }}>{error}</div>}

            <div style={{ display: 'flex', gap: 10 }}>
              <button
                type="button"
                disabled={!confirmTruckNumber || submitting}
                onClick={handleReceive}
                style={{ flex: 1, padding: '12px', borderRadius: 8, border: 'none', background: C.gold, color: '#0d1117', fontSize: 13, fontWeight: 700, cursor: confirmTruckNumber && !submitting ? 'pointer' : 'default', opacity: !confirmTruckNumber || submitting ? 0.5 : 1 }}
              >
                {submitting ? 'Verifying...' : 'Complete Transfer'}
              </button>
              <button
                type="button"
                onClick={() => { setReceivingTransferId(null); setConfirmTruckNumber(''); setError(null); }}
                style={{ padding: '12px 16px', borderRadius: 8, border: `1px solid ${C.borderSoft}`, background: 'transparent', color: C.textMuted, fontSize: 13, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
